import * as React from 'react'
import { Check, Loader2 } from 'lucide-react'
import type { OrganizationDto } from '@lib/dto/organization.dto'
import { SEASONS, SEASON_LABELS } from '@lib/enums/season.enum'
import {
  describeSeasonMonths,
  isCalendarMonth,
  seasonStartMonthsAreValid,
  type SeasonStartMonths,
} from '@lib/helpers/seasons'
import { updateOrganizationInputSchema } from '@lib/input/organization.input'
import { useUpdateOrganization } from '@/hooks/useOrganization'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { SELECT_CLASS } from '@/components/ui/field-classes'
import { errorMessage } from '@/helpers/logger'

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const

function sameMonths(a: SeasonStartMonths, b: SeasonStartMonths): boolean {
  return SEASONS.every((season) => a[season] === b[season])
}

/**
 * Which months make each season.
 *
 * Under Trees because seasons are how tree work is timed: a surveyor picks
 * "Spring" for bark banding, and these months are what decide whether that is
 * the next visit or a later one (docs/seasonal-work.md). A season runs until
 * the next one starts, so a start month for each is the whole setting.
 */
export function SeasonSettings({
  organization,
}: {
  organization: OrganizationDto
}) {
  const updateOrganization = useUpdateOrganization()
  const [draft, setDraft] = React.useState<SeasonStartMonths>(
    organization.seasonStartMonths,
  )
  /** Text, so a half-typed number is not snapped back under somebody. */
  const [leadText, setLeadText] = React.useState(
    String(organization.seasonReminderLeadDays),
  )
  const [error, setError] = React.useState<string | null>(null)
  const [saved, setSaved] = React.useState(false)

  const lead = updateOrganizationInputSchema.shape.seasonReminderLeadDays.safeParse(
    leadText.trim() === '' ? Number.NaN : Number(leadText),
  )
  const leadDays = lead.success ? (lead.data ?? null) : null
  const monthsAreValid = seasonStartMonthsAreValid(draft)
  const isDirty =
    !sameMonths(draft, organization.seasonStartMonths) ||
    leadDays !== organization.seasonReminderLeadDays
  const isValid = monthsAreValid && leadDays !== null

  async function handleSave(): Promise<void> {
    if (leadDays === null) return
    setError(null)
    setSaved(false)
    try {
      await updateOrganization.mutateAsync({
        seasonStartMonths: draft,
        seasonReminderLeadDays: leadDays,
      })
      setSaved(true)
    } catch (caught) {
      setError(errorMessage(caught, 'That could not be saved.'))
    }
  }

  return (
    <section className="space-y-3 rounded-lg border border-default bg-surface p-4">
      <div>
        <h2 className="text-lg font-semibold">Seasons</h2>
        <p className="mt-0.5 text-sm text-muted-text">
          The month each season starts. Work recorded for the season of the
          visit, or the next one with work, is what a proposal prices now; the
          rest is shown as future work.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {SEASONS.map((season) => {
          const id = `season-start-${season.toLowerCase()}`
          return (
            <div key={season} className="space-y-1">
              <label htmlFor={id} className="text-sm font-medium">
                {SEASON_LABELS[season]} starts in
              </label>
              <select
                id={id}
                className={SELECT_CLASS}
                value={draft[season]}
                onChange={(event) => {
                  const month = Number(event.target.value)
                  // The options are 1–12. Anything else is a tampered control,
                  // and it must not enter the draft as a month.
                  if (!isCalendarMonth(month)) return
                  setSaved(false)
                  setDraft({ ...draft, [season]: month })
                }}
              >
                {MONTH_NAMES.map((name, index) => (
                  <option key={name} value={index + 1}>
                    {name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-text">
                {monthsAreValid ? describeSeasonMonths(season, draft) : '\u00a0'}
              </p>
            </div>
          )
        })}
      </div>

      {monthsAreValid ? null : (
        <p className="text-sm text-danger">
          Seasons go winter, spring, summer, fall, each starting in a later
          month than the one before.
        </p>
      )}

      <div className="space-y-1">
        <label htmlFor="season-reminder-lead" className="text-sm font-medium">
          Remind us before a season starts
        </label>
        <div className="flex items-center gap-2">
          <Input
            id="season-reminder-lead"
            inputMode="numeric"
            className="w-20"
            aria-invalid={leadDays === null}
            value={leadText}
            onChange={(event) => {
              setSaved(false)
              setLeadText(event.target.value)
            }}
          />
          <span className="text-sm text-muted-text">days</span>
        </div>
        <p
          className={
            leadDays === null ? 'text-xs text-danger' : 'text-xs text-muted-text'
          }
        >
          {leadDays === null
            ? 'Enter a whole number of days, from 1 to 90.'
            : 'Work an accepted proposal estimated for a season joins Coming up on the Proposals page this long before the season starts, and its owner is emailed, so there is time to rebid it.'}
        </p>
      </div>
      {error === null ? null : <p className="text-sm text-danger">{error}</p>}

      <div className="flex items-center gap-3 border-t border-default pt-3">
        <Button
          disabled={!isDirty || !isValid || updateOrganization.isPending}
          onClick={() => {
            void handleSave()
          }}
        >
          {updateOrganization.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : null}
          Save changes
        </Button>
        {saved && !isDirty ? (
          <span className="flex items-center gap-1 text-sm text-muted-text">
            <Check className="h-3.5 w-3.5" />
            Saved
          </span>
        ) : null}
      </div>
    </section>
  )
}
